import { alpha } from 'foundations/theme';
import useTheme from 'hooks/useTheme';
import useWhiteLabelling from 'hooks/useWhitelabelling';
import React from 'react';
import { useSelector } from 'react-redux';
import styled from 'styled-components';
import { background, border, color, flexbox, space } from 'styled-system';
import { ExternalUrls } from 'utils/redirections';
import { Typography } from '../../../foundations/typography';
import { cdnImage } from '../../../utils/core';
import HomeFooter from '../../molecules/footer/footer';

export default function Footer({ custom, isDashboard }) {
    const {
        hidden,
        marginBottom = '0',
        showHomeV2Footer = false,
    } = useSelector((state) => state.layout.footer);

    // Bypass store managed footer state for custom mode
    const showFooter = custom ? true : hidden === false;

    const handleCTAClick = (destination) => {
        window.open(destination, '_self');
    };

    const { theme, isLightTheme } = useTheme();
    const {
        whiteLabelText,
        isWhiteLabellingEnabled: isEnabled,
        isAirmeetBrandingRestricted,
    } = useWhiteLabelling();
    const hostname = window?.location?.hostname || '';
    const isWhiteLabellingEnabled =
        isEnabled ||
        (hostname &&
            hostname !== '' &&
            !hostname.includes('airmeet.com') &&
            !hostname.includes('localhost'));

    return showFooter ? (
        <>
            <MainFooter
                className='h-footer'
                style={{ marginBottom, paddingBottom: 0 }}
                bg={isLightTheme && theme.colors.white}
                border={isLightTheme && 'none'}
            >
                <div className='container-fluid'>
                    {isDashboard ? (
                        <Box justifyContent='space-between' alignItems='center'>
                            <Box>
                                <Typography
                                    variant='caption'
                                    color='ambience.6'
                                    mr={3}
                                >
                                    {whiteLabelText('© 2022 Airmeet Inc.', '')}
                                </Typography>

                                <Typography
                                    variant='caption'
                                    color='ambience.6'
                                    mr={3}
                                >
                                    <a
                                        onClick={() =>
                                            handleCTAClick(
                                                ExternalUrls.PRIVACY_POLICY
                                            )
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Privacy Policy
                                    </a>
                                </Typography>

                                <Typography
                                    variant='caption'
                                    color='ambience.6'
                                >
                                    <a
                                        onClick={() =>
                                            handleCTAClick(
                                                ExternalUrls.TERMS_OF_USE
                                            )
                                        }
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Terms of Use
                                    </a>
                                </Typography>
                            </Box>
                            <div className='social-links'>
                                <a
                                    href={ExternalUrls.AIRMEET_FACEBOOK}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    <i className='fa fa-facebook-square'></i>
                                </a>
                                <a
                                    href={ExternalUrls.AIRMEET_TWITTER}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    <i
                                        className='fa fa-twitter-square'
                                        aria-hidden='true'
                                    ></i>
                                </a>
                                <a
                                    href={ExternalUrls.AIRMEET_LINKEDIN}
                                    target='_blank'
                                    rel='noopener noreferrer'
                                >
                                    <i
                                        className='fa fa-linkedin-square'
                                        aria-hidden='true'
                                    ></i>
                                </a>
                            </div>
                        </Box>
                    ) : (
                        <>
                            <div className='row'>
                                <div
                                    className='col-md-3 col-lg-2'
                                    style={{
                                        display: 'flex',
                                        justifyContent: 'center',
                                    }}
                                >
                                    <span className='logo'>
                                        {isWhiteLabellingEnabled ||
                                        isAirmeetBrandingRestricted ? null : (
                                            <img
                                                src={
                                                    isLightTheme
                                                        ? cdnImage(
                                                              '/images/airmeet-logo-light.svg'
                                                          )
                                                        : cdnImage(
                                                              '/images/airmeet-logo.svg'
                                                          )
                                                }
                                                alt='Airmeet'
                                                className='mw-100'
                                            />
                                        )}
                                    </span>
                                </div>
                                <FooterContainer className='col-md-6 col-lg-7'>
                                    <UnorderedList className='no-style f-nav-a'>
                                        <li>
                                            <Anchor
                                                onClick={() =>
                                                    handleCTAClick(
                                                        ExternalUrls.PRIVACY_POLICY
                                                    )
                                                }
                                                style={{ cursor: 'pointer' }}
                                                color={
                                                    isLightTheme
                                                        ? theme.colors
                                                              .ambience[4]
                                                        : theme.colors
                                                              .ambience[16]
                                                }
                                                role='link'
                                                tabIndex={0}
                                                aria-label='Airmeet Privacy Policy'
                                            >
                                                Privacy Policy
                                            </Anchor>
                                        </li>
                                        <li>
                                            <Anchor
                                                onClick={() =>
                                                    handleCTAClick(
                                                        ExternalUrls.TERMS_OF_USE
                                                    )
                                                }
                                                style={{ cursor: 'pointer' }}
                                                color={
                                                    isLightTheme
                                                        ? theme.colors
                                                              .ambience[4]
                                                        : theme.colors
                                                              .ambience[16]
                                                }
                                                role='link'
                                                tabIndex={0}
                                                aria-label='Airmeet Term of Use'
                                            >
                                                Terms of Use
                                            </Anchor>
                                        </li>
                                    </UnorderedList>
                                </FooterContainer>
                                <div className='col-md-3'>
                                    {!isWhiteLabellingEnabled ? (
                                        <div className='social-links'>
                                            <FocusableAnchorTag
                                                href={
                                                    ExternalUrls.AIRMEET_FACEBOOK
                                                }
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                aria-label='Open Airmeet Facebook Page'
                                            >
                                                <i className='fa fa-facebook-square'></i>
                                            </FocusableAnchorTag>
                                            <FocusableAnchorTag
                                                href={
                                                    ExternalUrls.AIRMEET_TWITTER
                                                }
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                aria-label='Open Airmeet Twitter Page'
                                            >
                                                <i
                                                    className='fa fa-twitter-square'
                                                    aria-hidden='true'
                                                ></i>
                                            </FocusableAnchorTag>
                                            <FocusableAnchorTag
                                                href={
                                                    ExternalUrls.AIRMEET_LINKEDIN
                                                }
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                aria-label='Open Airmeet Linkedin Page'
                                            >
                                                <i
                                                    className='fa fa-linkedin-square'
                                                    aria-hidden='true'
                                                ></i>
                                            </FocusableAnchorTag>
                                        </div>
                                    ) : null}
                                </div>
                            </div>
                            {!isWhiteLabellingEnabled ? (
                                <div className='row'>
                                    <div className='col-12'>
                                        <ul className='no-style f-nav-b'>
                                            <li className='a-rights text-center w-100'>
                                                <Typography
                                                    variant='caption'
                                                    color={
                                                        isLightTheme
                                                            ? theme.colors
                                                                  .ambience[4]
                                                            : theme.colors.white
                                                    }
                                                >
                                                    &copy; 2022 Airmeet Inc.{' '}
                                                </Typography>
                                            </li>
                                        </ul>
                                    </div>
                                </div>
                            ) : null}
                        </>
                    )}
                </div>
            </MainFooter>
            <footer className='main-footer d-none'>
                {/* <div className="container"> */}
                <div className='row logo-row'>
                    <div className=''>
                        <span className='logo'>
                            <img
                                src={cdnImage('/images/airmeet-logo.svg')}
                                alt='Airmeet'
                                className='mw-100'
                            />
                        </span>
                        <div className='social-links'>
                            <a
                                href={ExternalUrls.AIRMEET_TWITTER}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <i
                                    className='fa fa-twitter'
                                    aria-hidden='true'
                                ></i>
                            </a>
                            <a
                                href={ExternalUrls.AIRMEET_LINKEDIN}
                                target='_blank'
                                rel='noopener noreferrer'
                            >
                                <i
                                    className='fa fa-linkedin'
                                    aria-hidden='true'
                                ></i>
                            </a>
                        </div>
                    </div>
                    <div className=''>
                        <ul className='no-style f-nav-b'>
                            <li>
                                <a
                                    onClick={() =>
                                        handleCTAClick(
                                            ExternalUrls.PRIVACY_POLICY
                                        )
                                    }
                                    style={{ cursor: 'pointer' }}
                                >
                                    Privacy Policy
                                </a>
                            </li>
                            <li>
                                <a
                                    onClick={() =>
                                        handleCTAClick(
                                            ExternalUrls.TERMS_OF_USE
                                        )
                                    }
                                    style={{ cursor: 'pointer' }}
                                >
                                    Terms of Use
                                </a>
                            </li>
                        </ul>
                    </div>
                </div>
                <div className='row'>
                    <div className='col-12 a-rights'>
                        ©2021 Airmeet or its affiliates. All rights reserved.
                        Screen images simulated. User experience may vary.
                    </div>
                </div>
                {/* </div> */}
            </footer>
        </>
    ) : showHomeV2Footer ? (
        <HomeFooter />
    ) : null;
}

const Box = styled.div`
    ${space}
    ${flexbox}
    ${background}
    ${border}
    display: flex;
`;

const MainFooter = styled(Box)`
    border-top: ${({ theme }) =>
        theme.isLightTheme ? 'none' : `1px solid ${theme.colors.ambience[20]}`};
    position: ${({ theme }) => theme.isLightTheme && 'relative'};
    box-shadow: ${({ theme }) =>
        theme.isLightTheme &&
        `0px 0px 3.6px ${alpha(theme.colors.black, 0.11)},
        0px 0px 14.4px ${alpha(theme.colors.black, 0.04)}`};
`;

const Anchor = styled.span`
    ${color}
    &:focus-visible {
        outline: solid;
    }
`;

const FocusableAnchorTag = styled.a`
    &:focus {
        outline: none;
    }
    &:focus-visible {
        outline: solid;
    }
`;
const FooterContainer = styled.div`
    text-align: center;
`;

const UnorderedList = styled.ul`
    @media (min-width: 425px) {
        margin-left: 10em;
    }
`;
